// src/pages/CopywriterDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Grid, Box, Alert, CircularProgress, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

interface CopywriterStats {
  task_id: string;
  account_name: string;
  total_messages_received: number;
  total_messages_copied: number;
  total_messages_filtered: number;
  total_messages_failed: number;
  channel_stats: Record<string, {
    received: number;
    copied: number;
    filtered: number;
    failed: number;
  }>;
  last_successful_copy?: {
    channel: string;
    timestamp: string;
    has_media: boolean;
    text_length: number;
    is_album: boolean;
    original_text?: string;
    rewritten_text?: string;
  } | null;
  last_error?: {
    message: string;
    timestamp: string;
    channel?: string;
  } | null;
  started_at: string;
  last_activity_at?: string | null;
  uptime_seconds: number;
}

const CopywriterDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId?: string }>();
  const navigate = useNavigate();

  const [stats, setStats] = useState<CopywriterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Защита от отсутствия taskId
  useEffect(() => {
    if (!taskId) {
      navigate('/neurocopywriting');
    }
  }, [taskId, navigate]);

  // Функция загрузки
  const fetchStats = async () => {
    if (!taskId) return;

    try {
      const res = await api.get(`/copywrite/${taskId}/stats`);
      setStats(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Задача не найдена или удалена');
    } finally {
      setLoading(false);
    }
  };

  // Правильный useEffect — ВСЁ ЧЕРЕЗ fetchStats
  useEffect(() => {
    if (!taskId) return;

    fetchStats(); // Первый вызов

    const interval = setInterval(() => {
      fetchStats(); // Каждый тик
    }, 8000);

    return () => clearInterval(interval);
  }, [taskId]); // Зависимость только от taskId

  if (!taskId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики копирайтера...</Typography>
      </Container>
    );
  }

  if (error || !stats) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Задача не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/neurocopywriting')}>
          Назад к нейрокопирайтерам
        </Button>
      </Container>
    );
  }

  const uptime = stats.uptime_seconds;
  const hours = Math.floor(uptime / 3600);
  const minutes = Math.floor((uptime % 3600) / 60);
  const seconds = uptime % 60;

  const lastCopy = stats.last_successful_copy;

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/neurocopywriting')} sx={{ mb: 4 }}>
        Назад к нейрокопирайтерам
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Нейрокопирайтер #{taskId.slice(0, 8)}
        </Typography>

        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          Аккаунт: <strong>{stats.account_name}</strong> ·
          <AccessTimeIcon sx={{ verticalAlign: 'middle', mx: 1 }} />
          Время работы: <strong>{hours}ч {minutes}м {seconds}с</strong>
        </Typography>

        {/* Основные счётчики */}
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {stats.total_messages_copied}
                </Typography>
                <Typography color="text.secondary">Скопировано</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#8B5CF6">
                  {stats.total_messages_received}
                </Typography>
                <Typography color="text.secondary">Получено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#F59E0B">
                  {stats.total_messages_filtered}
                </Typography>
                <Typography color="text.secondary">Отфильтровано</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#DC2626">
                  {stats.total_messages_failed}
                </Typography>
                <Typography color="text.secondary">Ошибок</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* По каналам */}
        {Object.keys(stats.channel_stats).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Статистика по каналам-источникам
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Канал</strong></TableCell>
                    <TableCell align="right"><strong>Получено</strong></TableCell>
                    <TableCell align="right"><strong>Скопировано</strong></TableCell>
                    <TableCell align="right"><strong>Фильтр</strong></TableCell>
                    <TableCell align="right"><strong>Ошибки</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(stats.channel_stats).map(([channel, s]) => (
                    <TableRow key={channel}>
                      <TableCell>{channel}</TableCell>
                      <TableCell align="right">{s.received}</TableCell>
                      <TableCell align="right" sx={{ color: '#10B981', fontWeight: 600 }}>{s.copied}</TableCell>
                      <TableCell align="right">{s.filtered}</TableCell>
                      <TableCell align="right" sx={{ color: s.failed > 0 ? '#DC2626' : 'inherit' }}>
                        {s.failed}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Последний скопированный пост */}
        {lastCopy && (
          <Box sx={{ mb: 6, p: 5, bgcolor: '#F0FDF4', borderRadius: 3, border: '2px solid #86EFAC' }}>
            <Typography variant="h6" color="#166534" gutterBottom>
              Последний скопированный пост
            </Typography>
            <Typography fontSize="0.95rem" color="#166534" gutterBottom>
              из <strong>{lastCopy.channel}</strong>
            </Typography>

            {lastCopy.rewritten_text ? (
              <>
                <Typography fontWeight={600} color="#166534" mt={3} mb={1}>
                  Переписанный текст:
                </Typography>
                <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 2, border: '1px solid #BBF7D0' }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '1.1rem', lineHeight: 1.7 }}>
                    {lastCopy.rewritten_text}
                  </Typography>
                </Box>

                {lastCopy.original_text && lastCopy.original_text !== lastCopy.rewritten_text && (
                  <>
                    <Typography fontWeight={600} color="#92400E" mt={3} mb={1}>
                      Исходный текст:
                    </Typography>
                    <Box sx={{ p: 3, bgcolor: '#FEF3C7', borderRadius: 2, border: '1px dashed #F59E0B' }}>
                      <Typography sx={{ whiteSpace: 'pre-wrap', opacity: 0.9 }}>
                        {lastCopy.original_text}
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            ) : lastCopy.original_text ? (
              <>
                <Typography fontWeight={600} color="#166534" mt={3} mb={1}>
                  Текст поста:
                </Typography>
                <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 2 }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '1.1rem', lineHeight: 1.7 }}>
                    {lastCopy.original_text}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography mt={2} fontStyle="italic" color="#166534">
                {lastCopy.is_album ? 'Альбом' : 'Медиа'} без текста {lastCopy.has_media && '(фото/видео)'}
              </Typography>
            )}

            <Typography fontSize="0.9rem" color="text.secondary" mt={3}>
              {new Date(lastCopy.timestamp).toLocaleString('ru-RU')}
            </Typography>
          </Box>
        )}

        {/* Ошибка */}
        {stats.last_error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            <strong>Последняя ошибка:</strong> {stats.last_error.message}
            {stats.last_error.channel && ` (канал: ${stats.last_error.channel})`}
            <br />
            <Typography component="span" fontSize="0.85rem">
              {new Date(stats.last_error.timestamp).toLocaleString('ru-RU')}
            </Typography>
          </Alert>
        )}

        {/* Время работы */}
        <Grid container spacing={3} sx={{ mt: 4, color: '#4B5563' }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <strong>Запущен:</strong> {new Date(stats.started_at).toLocaleString('ru-RU')}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <strong>Время работы:</strong> {hours}ч {minutes}м {seconds}с
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <strong>Последняя активность:</strong>{' '}
            {stats.last_activity_at
              ? new Date(stats.last_activity_at).toLocaleString('ru-RU')
              : '—'}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default CopywriterDetail;